/**
 * Signing back in without losing the ledger.
 *
 * A sheet rather than a screen, because the app is still perfectly usable behind
 * it — the user was in the middle of something, and a dead session is not a
 * reason to throw that away. Same overlay idiom as `ConfirmSheet`: bottom sheet
 * on mobile, centred full-bleed band on desktop, hairlines, no card (D-1).
 *
 * It renders Google's own button and hands the session to `applySession`
 * (through `useAdoptSession`), which keeps the local ledger when the same
 * account signs back in, clears it silently when another account's ledger has
 * nothing pending, and opens the switch sheet when it does. Nothing navigates (INV-8) — on iOS an
 * installed PWA and Safari have separate storage jars, so a redirect flow can
 * write the session somewhere this app will never read it.
 */

import { Sheet } from "@/features/common/sheet";

import { useAdoptSession } from "@/features/onboarding/leave_flow";
import { SignInButtons } from "@/features/onboarding/sign_in_buttons";
import { useAppleSignIn, useGoogleSignIn } from "@/hooks/use_sign_in";

const TITLE = "Sign in again";

export function ReauthSheet({ onClose }: { onClose: () => void }): React.JSX.Element {
  // Adopted closes the sheet; another account's pending changes open the
  // switch sheet on top of it; a failure stays here with a Try again (INV-7).
  const { adopt, failure, clearFailure, sheet } = useAdoptSession(onClose);
  const google = useGoogleSignIn(adopt);
  const apple = useAppleSignIn(adopt);
  const { status, error, retry } =
    apple.status !== "ready" ? { status: apple.status, error: apple.error, retry: apple.reset } : google;
  const switching = sheet !== null;

  return (
    <>
      <Sheet openHaptic={false} title={TITLE} onClose={onClose} blockKeys escapeDisabled={switching}>
        {(close) => (
          <div className="mx-auto flex w-full max-w-md flex-col gap-phi-0 px-phi-1">
            <div>
              {/* Plain, and specific about what is and is not at risk. The tide
                  does not come into it — this is a control (D-7, C-8). */}
              <p className="pt-phi-n6 text-phi-n1 text-tx-2">
                Your session ended, so nothing is uploading. Your ledger lives in your account, and
                anything logged since is saved on this device until you are back in.
              </p>
            </div>

            <div className="flex min-h-[44px] flex-col items-center gap-phi-n1">
              <SignInButtons google={google} apple={apple} />
              {status === "loading" ? <p className="text-phi-n1 text-tx-2">Loading sign-in…</p> : null}
              {/* Not while a failure is showing: `status` belongs to the Google
                  exchange, which has already succeeded by then, so it would
                  otherwise say the sign-in is still in progress next to the
                  message saying it failed. */}
              {status === "signing-in" && !failure ? (
                <p className="text-phi-n1 text-tx-2">Signing you in…</p>
              ) : null}
              {(status === "error" && error) || failure ? (
                <div className="flex flex-col items-center gap-phi-n3">
                  <p role="alert" data-feedback="error" className="max-w-phi-12 text-center text-phi-n1 text-over">
                    {failure ?? error}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      clearFailure();
                      retry();
                    }}
                    className="min-h-[44px] text-phi-n1 font-medium text-acc"
                  >
                    Try again
                  </button>
                </div>
              ) : null}
            </div>

            <button type="button" onClick={close} className="min-h-[44px] text-phi-n1 text-tx-2">
              Not now
            </button>
          </div>
        )}
      </Sheet>
      {/* A sibling, not a child: a tap on its backdrop must not bubble into this
          sheet's own backdrop and close both. */}
      {sheet}
    </>
  );
}
